import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout from './LegalLayout.jsx';

const Mail = () => (
  <a href="mailto:ceo@civixcrm.ai">ceo@civixcrm.ai</a>
);

export default function Terms() {
  return (
    <LegalLayout title="Terms of Service — CIVIX">
      <h1>Terms of Service</h1>
      <p className="legal-meta">
        <strong>Effective date:</strong> May 13, 2026<br />
        <strong>Last updated:</strong> May 13, 2026
      </p>

      <p>
        These Terms of Service ("Terms") govern your access to and use of the CIVIX customer
        relationship management platform available at civixcrm.ai and related applications (the
        "Service"), operated by CIVIX LLC, a Colorado limited liability company ("CIVIX," "we,"
        "our," or "us"). By creating an account or using the Service, you agree to be bound by these
        Terms.
      </p>

      <h2>1. Eligibility</h2>
      <p>
        You may use the Service only if you are at least 18 years old, located in the United States,
        and authorized to conduct business in each U.S. state in which you intend to contact leads.
        Insurance agents and agencies using the Service to sell, solicit, or quote insurance products
        must hold an active license in good standing in every state in which they engage prospects.
        By using the Service you represent that all licensing representations made in your account
        are true and current.
      </p>

      <h2>2. Accounts and tenancy</h2>
      <p>
        When you register, CIVIX provisions a tenant for your business. You are responsible for all
        activity that occurs under your tenant, for keeping credentials confidential, and for
        ensuring that everyone you invite to your tenant complies with these Terms. You agree to
        provide accurate, complete information and to keep it up to date.
      </p>

      <h2>3. Acceptable use</h2>
      <p>You agree NOT to use the Service to:</p>
      <ul>
        <li>Send any SMS or place any outbound call to a recipient who has not provided lawful prior express written consent under the Telephone Consumer Protection Act (47 U.S.C. § 227) and applicable state law</li>
        <li>Contact a number listed on the Federal Do Not Call Registry without an established business relationship or written consent</li>
        <li>Send messages or place calls outside the hours of 8:00 a.m. and 9:00 p.m. in the recipient's local time</li>
        <li>Send unlawful, harassing, fraudulent, defamatory, infringing, obscene, or sexually explicit content</li>
        <li>Promote loan-sharking, illegal substances, firearms sales to consumers, or any other content prohibited by the carrier industry's A2P 10DLC guidelines</li>
        <li>Upload leads you do not lawfully own or have a right to contact</li>
        <li>Misrepresent the identity of the sender or your relationship to a recipient</li>
        <li>Attempt to gain unauthorized access to another tenant's data, the underlying infrastructure, or any other account</li>
        <li>Reverse engineer, decompile, or scrape the Service except as expressly permitted by law</li>
        <li>Use the Service to compete with CIVIX or to build a competing product</li>
      </ul>
      <p>
        You acknowledge that CIVIX enforces automated compliance gates (TCPA consent, DNC scrub,
        quiet hours, state licensing) and that any attempt to bypass these gates is a material breach
        of these Terms.
      </p>

      <h2>4. Customer responsibilities under TCPA and state law</h2>
      <p>
        You are the message sender and caller under the TCPA. CIVIX is your platform provider. You
        are solely responsible for:
      </p>
      <ul>
        <li>Obtaining and retaining proof of prior express written consent for every lead before any contact</li>
        <li>Scrubbing your lead lists against the Federal DNC Registry and any applicable state DNC lists</li>
        <li>Honoring opt-out requests immediately (CIVIX enforces this automatically when a recipient texts STOP through the platform)</li>
        <li>Maintaining current licensing in every state in which you contact prospects</li>
        <li>Complying with state-specific telemarketing, insurance, and consumer-protection laws</li>
      </ul>
      <p>CIVIX provides tools to help you comply; CIVIX does not guarantee your compliance.</p>

      <h2>5. Fees and billing</h2>
      <p>
        Subscription fees, per-message fees, and per-minute fees are described in your subscription
        confirmation and on your billing dashboard. Fees are charged in U.S. dollars via Stripe. You
        authorize CIVIX to charge your payment method on each renewal until you cancel. Fees are
        non-refundable except as required by law.
      </p>

      <h2>6. Suspension and termination</h2>
      <p>CIVIX may suspend or terminate your access immediately, with or without notice, if:</p>
      <ul>
        <li>You materially breach these Terms (including any acceptable-use violation)</li>
        <li>We receive a credible report of TCPA, DNC, or telemarketing-law violations originating from your tenant</li>
        <li>A carrier, regulator, or law-enforcement body requires suspension</li>
        <li>Your payment method fails and is not corrected within 7 days</li>
        <li>We exercise the CIVIX "outbound kill-switch" for your tenant to protect carrier relationships or prevent immediate harm</li>
      </ul>
      <p>
        You may terminate your subscription at any time from the billing dashboard. Upon termination,
        your right to access the Service ends. We will retain your data for at least 30 days
        post-termination during which you may request an export.
      </p>

      <h2>7. Intellectual property</h2>
      <p>
        CIVIX and its licensors own all right, title, and interest in the Service, including all
        software, design, trademarks, and documentation. You receive a limited, non-exclusive,
        non-transferable, revocable license to use the Service in accordance with these Terms. You
        retain all rights in your data; you grant CIVIX a non-exclusive license to host, store, and
        process your data solely to provide the Service.
      </p>

      <h2>8. Third-party integrations</h2>
      <p>
        The Service integrates with third-party services (including Stripe, Twilio, Meta, Google,
        Ringy, GoHighLevel, and others). Your use of those services is subject to their own terms and
        privacy policies. CIVIX is not responsible for any third-party service.
      </p>

      <h2>9. Disclaimer</h2>
      <p>
        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND. CIVIX
        DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A
        PARTICULAR PURPOSE, NON-INFRINGEMENT, AND ANY WARRANTY REGARDING REGULATORY COMPLIANCE
        OUTCOMES.
      </p>

      <h2>10. Limitation of liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, CIVIX SHALL NOT BE LIABLE FOR ANY INDIRECT,
        INCIDENTAL, CONSEQUENTIAL, SPECIAL, PUNITIVE, OR EXEMPLARY DAMAGES, OR FOR LOST PROFITS,
        REVENUE, GOODWILL, OR DATA, ARISING OUT OF OR IN CONNECTION WITH THE SERVICE. CIVIX'S
        AGGREGATE LIABILITY FOR ALL CLAIMS RELATED TO THE SERVICE SHALL NOT EXCEED THE FEES PAID BY
        YOU TO CIVIX IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
      </p>

      <h2>11. Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold harmless CIVIX, its officers, directors, employees,
        and agents from and against any claims, damages, liabilities, costs, and fees (including
        reasonable attorneys' fees) arising from (a) your use of the Service, (b) your violation of
        these Terms, (c) your violation of any law (including the TCPA, DNC laws, or insurance
        regulations), or (d) any content you submit to the Service.
      </p>

      <h2>12. Governing law and dispute resolution</h2>
      <p>
        These Terms are governed by the laws of the State of Colorado, without regard to
        conflict-of-laws principles. Any dispute arising under these Terms shall be resolved
        exclusively in the state or federal courts located in Arapahoe County, Colorado, and you
        consent to personal jurisdiction in those courts. Each party waives any right to a jury
        trial.
      </p>

      <h2>13. Changes to these Terms</h2>
      <p>
        We may modify these Terms from time to time. We will post the updated Terms with a new "Last
        updated" date. Material changes will be communicated via email or in-app notice at least 14
        days before they take effect. Your continued use after that date constitutes acceptance.
      </p>

      <h2>14. Contact</h2>
      <p>
        CIVIX LLC<br />
        5600 S Quebec St Suite 300<br />
        Greenwood Village, CO 80111<br />
        Email: <Mail /><br />
        Phone: +1 (719) 387-0657
      </p>

      <p>
        See also: <Link to="/privacy">Privacy Policy</Link> · <Link to="/sms-policy">SMS Policy</Link>
      </p>
    </LegalLayout>
  );
}
